import { useCallback, useEffect, useMemo, useState } from "react";

import { CheckCircle, Copy, Plus } from "lucide-react";
import { toast } from "sonner";

import ActionMenu from "../../../components/shared/ActionMenu";
import DataTable from "../../../components/shared/DataTable";
import Pagination from "../../../components/shared/Pagination";
import SearchInput from "../../../components/shared/SearchInput";
import StatusBadge from "../../../components/shared/StatusBadge";
import TruncatedText from "../../../components/shared/TruncatedText";
import WordCount from "../../../components/shared/WordCount.jsx";
import { Button } from "../../../components/ui/button.jsx";
import { Checkbox } from "../../../components/ui/checkbox.jsx";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog.jsx";
import { DropdownMenuItem } from "../../../components/ui/dropdown-menu";
import { Input } from "../../../components/ui/input.jsx";
import { Label } from "../../../components/ui/label.jsx";
import { Progress } from "../../../components/ui/progress.jsx";
import { Textarea } from "../../../components/ui/textarea.jsx";
import { useApi } from "../../../context/ApiContext";
import { useAuth } from "../../../context/AuthContext";
import { useConfirm } from "../../../context/ConfirmContext.jsx";
import { usePagination } from "../../../hooks/usePagination";
import { cn, getCharCount } from "../../../lib/utils.js";
import { formatDate, formatDateForInput } from "../../../utils/dateUtils";

const MilestonesTab = ({ selectedProject }) => {
  const api = useApi();
  const { user } = useAuth();
  const pagination = usePagination(10);
  const { items: milestones, currentPage: page, pageSize, setPaginationData } = pagination;
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isAddMilestoneOpen, setIsAddMilestoneOpen] = useState(false);
  const [newMilestone, setNewMilestone] = useState({
    name: "",
    description: "",
    startDate: "",
    targetDate: ""
  });
  const [activeSearchText, setActiveSearchText] = useState("");
  const [includeTasks, setIncludeTasks] = useState(false);
  const [originalMilestoneId, setOriginalMilestoneId] = useState(null);
  const [sortBy, setSortBy] = useState("createdAt");
  const [direction, setDirection] = useState("desc");

  const permissions = useMemo(
    () => ({
      canCreate: user?.permissions?.milestone?.create && !selectedProject?.deleted,
      canUpdate: user?.permissions?.milestone?.update && !selectedProject?.deleted,
      canDelete: user?.permissions?.milestone?.delete && !selectedProject?.deleted
    }),
    [user?.permissions?.milestone, selectedProject?.deleted]
  );

  const isNewDescriptionOverLimit = getCharCount(newMilestone.description) > 500;
  const isEditDescriptionOverLimit = editingMilestone ? getCharCount(editingMilestone.description) > 500 : false;

  const loadMilestones = useCallback(
    async (projectId) => {
      try {
        const response = await api.get(`/milestones/${projectId}/search`, {
          params: {
            page,
            size: pageSize,
            search: activeSearchText,
            sortBy: sortBy || undefined,
            direction: direction || undefined
          }
        });
        setPaginationData(response.data);
      } catch (error) {
        console.error("Failed to load milestones:", error);
      }
    },
    [api, page, pageSize, setPaginationData, activeSearchText, sortBy, direction]
  );

  const addCustomMilestone = async (e) => {
    e.preventDefault();
    if (isNewDescriptionOverLimit) {
      toast.error("Description exceeds the 500-character limit.");
      return;
    }

    try {
      await api.post(`/milestones`, {
        projectId: selectedProject.id,
        name: newMilestone.name,
        description: newMilestone.description,
        startDate: newMilestone.startDate ? new Date(newMilestone.startDate).toISOString() : null,
        targetDate: newMilestone.targetDate ? new Date(newMilestone.targetDate).toISOString() : null
      });
      loadMilestones(selectedProject.id);
      setNewMilestone({
        name: "",
        description: "",
        startDate: "",
        targetDate: ""
      });
      setIsAddMilestoneOpen(false);
      toast.success("Milestone added successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to add milestone");
    }
  };

  const updateMilestoneProgress = async (milestoneId, completionPercentage, status) => {
    try {
      await api.put(`/milestones/status/${milestoneId}`, {
        completionPercentage,
        status,
        actualCompletionDate: status === "completed" ? new Date().toISOString() : null
      });
      loadMilestones(selectedProject.id);
      toast.success("Milestone updated!");
    } catch (error) {
      toast.error("Failed to update milestone");
    }
  };

  const updateMilestone = async (milestoneId, updates) => {
    try {
      await api.put(`/milestones/${milestoneId}`, updates);
      loadMilestones(selectedProject.id);
      setEditingMilestone(null);
      toast.success("Milestone updated successfully!");
    } catch (error) {
      toast.error("Failed to update milestone");
    }
  };

  const confirm = useConfirm();

  const deleteMilestone = async (milestoneId) => {
    const isConfirmed = await confirm({
      title: "Delete Milestone",
      message: "Are you sure you want to delete this milestone?"
    });
    if (isConfirmed) {
      try {
        await api.delete(`/milestones/${milestoneId}`);
        loadMilestones(selectedProject.id);
        toast.success("Milestone deleted successfully!");
      } catch (error) {
        toast.error(error.message || "Failed to delete milestone");
      }
    }
  };

  const duplicateMilestone = (milestone) => {
    // Instead of immediate backend call, we open the edit modal with a copy
    setOriginalMilestoneId(milestone.id);
    setEditingMilestone({
      ...milestone,
      id: null, // Clear ID so it's treated as new
      name: `${milestone.name} (Copy)`,
      status: "not_started",
      completionPercentage: 0,
      actualCompletionDate: null
    });
    setIsDuplicating(true);
    setIncludeTasks(false);
  };

  useEffect(() => {
    if (selectedProject?.id) {
      loadMilestones(selectedProject.id);
    }
  }, [selectedProject, loadMilestones]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-500" />
          Project Milestones
        </h3>

        <div className="flex flex-col sm:flex-row items-center gap-2 w-full lg:w-auto">
          <SearchInput placeholder="Search milestones..." onSearch={setActiveSearchText} className="sm:w-64" />

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Dialog open={isAddMilestoneOpen} onOpenChange={setIsAddMilestoneOpen}>
              {permissions.canCreate && (
                <Button
                  variant="primary"
                  className="h-[42px] px-4 whitespace-nowrap rounded-lg flex items-center gap-2 shadow-sm hover:shadow-md transition-all active:scale-95"
                  onClick={() => setIsAddMilestoneOpen(true)}
                >
                  <Plus className="w-4 h-4" />
                  <span className="font-medium">Add Milestone</span>
                </Button>
              )}
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Custom Milestone</DialogTitle>
                  <DialogDescription>Create a custom milestone for this project</DialogDescription>
                </DialogHeader>
                <form onSubmit={addCustomMilestone} className="space-y-4">
                  <div>
                    <Label htmlFor="milestone_name">Milestone Name</Label>
                    <Input
                      id="milestone_name"
                      value={newMilestone.name}
                      onChange={(e) =>
                        setNewMilestone({
                          ...newMilestone,
                          name: e.target.value
                        })
                      }
                      placeholder="e.g., HVAC Installation"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="milestone_description">Description</Label>
                    <Textarea
                      id="milestone_description"
                      value={newMilestone.description}
                      onChange={(e) =>
                        setNewMilestone({
                          ...newMilestone,
                          description: e.target.value
                        })
                      }
                      className={cn(isNewDescriptionOverLimit && "border-red-500")}
                      rows={2}
                      placeholder="Detailed description of the milestone"
                    />
                    <WordCount text={newMilestone.description} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="milestone_start_date">Start Date</Label>
                      <Input
                        id="milestone_start_date"
                        type="date"
                        value={newMilestone.startDate}
                        min={formatDateForInput(selectedProject?.startDate)}
                        max={formatDateForInput(selectedProject?.endDate)}
                        onChange={(e) =>
                          setNewMilestone({
                            ...newMilestone,
                            startDate: e.target.value
                          })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="milestone_target_date">Target Date</Label>
                      <Input
                        id="milestone_target_date"
                        type="date"
                        value={newMilestone.targetDate}
                        min={newMilestone.startDate || formatDateForInput(selectedProject?.startDate)}
                        max={formatDateForInput(selectedProject?.endDate)}
                        onChange={(e) =>
                          setNewMilestone({
                            ...newMilestone,
                            targetDate: e.target.value
                          })
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="flex justify-end pt-6">
                    <Button
                      variant="primary"
                      type="submit"
                      className="w-full py-6 rounded-lg"
                      disabled={isNewDescriptionOverLimit}
                    >
                      Add Milestone
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Dialog
          open={!!editingMilestone}
          onOpenChange={(open) => {
            if (!open) {
              setEditingMilestone(null);
              setIsDuplicating(false);
              setOriginalMilestoneId(null);
              setIncludeTasks(false);
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{isDuplicating ? "Duplicate Milestone" : "Edit Milestone"}</DialogTitle>
              <DialogDescription>
                {isDuplicating
                  ? "Modify details to create a new duplicate milestone"
                  : "Update milestone details for this project"}
              </DialogDescription>
            </DialogHeader>
            {editingMilestone && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (isEditDescriptionOverLimit) return;

                  if (isDuplicating) {
                    api
                      .post(`/milestones/duplicate/${originalMilestoneId}?includeTasks=${includeTasks}`, {
                        name: editingMilestone.name,
                        description: editingMilestone.description,
                        startDate: editingMilestone.startDate ? new Date(editingMilestone.startDate).toISOString() : null,
                        targetDate: editingMilestone.targetDate ? new Date(editingMilestone.targetDate).toISOString() : null
                      })
                      .then(() => {
                        loadMilestones(selectedProject.id);
                        setEditingMilestone(null);
                        setIsDuplicating(false);
                        setOriginalMilestoneId(null);
                        setIncludeTasks(false);
                        toast.success("Milestone duplicated successfully!");
                      })
                      .catch((err) => {
                        toast.error(err.message || "Failed to duplicate");
                      });
                  } else {
                    updateMilestone(editingMilestone.id, {
                      name: editingMilestone.name,
                      description: editingMilestone.description,
                      startDate: editingMilestone.startDate ? new Date(editingMilestone.startDate).toISOString() : null,
                      targetDate: editingMilestone.targetDate ? new Date(editingMilestone.targetDate).toISOString() : null
                    });
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <Label htmlFor="edit_milestone_name">Milestone Name</Label>
                  <Input
                    id="edit_milestone_name"
                    value={editingMilestone.name}
                    onChange={(e) =>
                      setEditingMilestone({
                        ...editingMilestone,
                        name: e.target.value
                      })
                    }
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit_milestone_description">Description</Label>
                  <Textarea
                    id="edit_milestone_description"
                    value={editingMilestone.description || ""}
                    onChange={(e) =>
                      setEditingMilestone({
                        ...editingMilestone,
                        description: e.target.value
                      })
                    }
                    className={cn(isEditDescriptionOverLimit && "border-red-500")}
                    rows={2}
                  />
                  <WordCount text={editingMilestone.description} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit_milestone_start_date">Start Date</Label>
                    <Input
                      id="edit_milestone_start_date"
                      type="date"
                      value={editingMilestone.startDate ? editingMilestone.startDate.split("T")[0] : ""}
                      min={formatDateForInput(selectedProject?.startDate)}
                      max={formatDateForInput(selectedProject?.endDate)}
                      onChange={(e) =>
                        setEditingMilestone({
                          ...editingMilestone,
                          startDate: e.target.value
                        })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_milestone_target_date">Target Date</Label>
                    <Input
                      id="edit_milestone_target_date"
                      type="date"
                      value={editingMilestone.targetDate ? editingMilestone.targetDate.split("T")[0] : ""}
                      min={
                        editingMilestone.startDate
                          ? editingMilestone.startDate.split("T")[0]
                          : formatDateForInput(selectedProject?.startDate)
                      }
                      max={formatDateForInput(selectedProject?.endDate)}
                      onChange={(e) =>
                        setEditingMilestone({
                          ...editingMilestone,
                          targetDate: e.target.value
                        })
                      }
                      required
                    />
                  </div>
                </div>

                {isDuplicating && (
                  <div className="flex items-center space-x-3 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800 transition-all hover:bg-slate-100 dark:hover:bg-slate-900">
                    <Checkbox
                      id="includeTasks"
                      checked={includeTasks}
                      onCheckedChange={setIncludeTasks}
                      className="h-5 w-5 rounded border-slate-300 text-yale-blue-600 focus:ring-yale-blue-500"
                    />
                    <div className="grid gap-1.5 leading-none">
                      <label
                        htmlFor="includeTasks"
                        className="text-sm font-bold text-slate-700 dark:text-slate-200 cursor-pointer"
                      >
                        Duplicate all tasks
                      </label>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Check this to clone existing tasks into the new milestone.
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-6">
                  <Button
                    variant="primary"
                    type="submit"
                    className="w-full py-6 rounded-lg"
                    disabled={isEditDescriptionOverLimit}
                  >
                    {isDuplicating ? "Duplicate Milestone" : "Save Changes"}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <DataTable
          columns={[
            {
              header: "Milestone",
              accessor: "name",
              isPrimary: true,
              wrap: true,
              headerClassName: "w-[250px]",
              render: (val, row) => (
                <div className="flex flex-col gap-1 max-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold text-yale-blue-900 dark:text-slate-100 whitespace-normal break-words">
                      {val}
                    </span>
                    {row.deleted && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 uppercase tracking-wider shrink-0">
                        Deleted
                      </span>
                    )}
                  </div>
                  {row.description && (
                    <TruncatedText text={row.description} maxLength={45} className="text-xs text-slate-500 dark:text-slate-400" />
                  )}
                </div>
              )
            },
            {
              header: "Start Date",
              accessor: "startDate",
              sortable: true,
              sortKey: "startDate",
              render: (val) => <span className="text-sm text-slate-600 dark:text-slate-400">{formatDate(val)}</span>
            },
            {
              header: "Target Date",
              accessor: "targetDate",
              sortable: true,
              sortKey: "targetDate",
              render: (val) => <span className="text-sm text-slate-600 dark:text-slate-400">{formatDate(val)}</span>
            },
            {
              header: "Progress",
              accessor: "completionPercentage",
              render: (val) => (
                <div className="w-32 md:w-40 space-y-1.5">
                  <div className="flex justify-between text-[10px] font-medium text-slate-500">
                    <span>Progress</span>
                    <span>{val}%</span>
                  </div>
                  <Progress value={val} className="h-1.5" />
                </div>
              )
            },
            {
              header: "Status",
              accessor: "status",
              render: (val) => <StatusBadge status={val} />
            },
            {
              header: "Created By",
              accessor: "creator",
              render: (val) => (
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">{val?.name || "System"}</span>
              )
            },
            {
              header: "Actions",
              accessor: "id",
              render: (_, milestone) => (
                <ActionMenu
                  canEdit={permissions.canUpdate && !milestone.deleted}
                  canDelete={permissions.canDelete && !milestone.deleted}
                  onEdit={() => setEditingMilestone(milestone)}
                  onDelete={() => deleteMilestone(milestone.id)}
                  menuWidth="w-56"
                  customActions={
                    <>
                      {permissions.canCreate && !milestone.deleted && (
                        <DropdownMenuItem onClick={() => duplicateMilestone(milestone)} className="cursor-pointer">
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                      )}
                      {permissions.canUpdate && !milestone.deleted && (
                        <>
                          <div className="h-px bg-slate-100 dark:bg-slate-800 my-1" />
                          {[25, 50, 75].map((pct) => (
                            <DropdownMenuItem
                              key={pct}
                              onClick={() => updateMilestoneProgress(milestone.id, pct, "in_progress")}
                              className="cursor-pointer"
                            >
                              Update to {pct}%
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuItem
                            onClick={() => updateMilestoneProgress(milestone.id, 100, "completed")}
                            className="cursor-pointer text-emerald-600 focus:text-emerald-600 font-semibold"
                          >
                            Mark as Completed
                          </DropdownMenuItem>
                        </>
                      )}
                    </>
                  }
                />
              )
            }
          ]}
          data={milestones}
          emptyStateTitle="No milestones found"
          emptyStateDescription="Break down your project into key milestones."
          emptyStateIcon={CheckCircle}
          sortBy={sortBy}
          direction={direction}
          onSort={(key, dir) => {
            setSortBy(key);
            setDirection(dir);
          }}
        />
      </div>

      <Pagination pagination={pagination} />
    </div>
  );
};

export default MilestonesTab;
